import moment from 'moment';

import { BlockUI, NgBlockUI } from 'ng-block-ui';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  Input,
  OnInit,
  ViewChild
}                             from '@angular/core';
import { NgbActiveModal }     from '@ng-bootstrap/ng-bootstrap';
import { NgForm }             from '@angular/forms';
import { UIRouter }           from '@uirouter/core';

import { ApiService }                     from '../../app-lite/api/api.service';
import { ChargifyService }                from '../../app-lite/chargify/chargify.service';
import { CodeTypeModel }                  from '../../app-lite/core/models/code-type';
import { CompanyCountryValidationsModel } from '../../app-lite/companies/models/company.country-validations';
import { CompanyService }                 from './company.service';
import { CompanySettingsModel }           from '../../app-lite/companies/models/company.settings';
import { FlipperSidesModel }              from '../../app-lite/shared/components/flipper/typings/flipper.sides';
import { UtcOffset }                      from '../../app-lite/companies/models/utc-offset';

@Component({
  selector   : 'wor-company-set-up',
  template: require('./company.set-up.component.html')
})
export class CompanySetUpComponent implements OnInit {
  activeSide     : FlipperSidesModel = 'front';
  dateTimeFormat = 'YYYY-MM-DDTHH:mm:ss';
  states         : Array<CodeTypeModel>;
  taxGroup       : any = {};
  timeFormat     = 'HH:mm:ss';
  utcOffsets     : Array<UtcOffset>;

  @BlockUI('companySetUpBackBlock') backBlock   : NgBlockUI;
  @BlockUI('companySetUpFrontBlock') frontBlock : NgBlockUI;

  @Input() company        : any;
  @Input() companySettings: CompanySettingsModel;
  @Input() countries      : Array<CodeTypeModel>;
  @Input() languages      : Array<CodeTypeModel>;
  @Input() validations    : CompanyCountryValidationsModel;

  @ViewChild('backForm') public backForm  : NgForm;
  @ViewChild('frontForm') public frontForm: NgForm;

  constructor (
    private activeModal      : NgbActiveModal,
    private apiService       : ApiService,
    private changeDetectorRef: ChangeDetectorRef,
    private chargifyService  : ChargifyService,
    @Inject(forwardRef(() => CompanyService))
    public companyService    : CompanyService,
    private router           : UIRouter
  ) {}

  ngOnInit () : void {
    if ( this.company.country_code ) {
      this.validations = this.validations[this.company.country_code];

      this.getStates()
      .then(( states : Array<CodeTypeModel> ) => {
        this.states = states;

        if ( this.company.state ) {
          this.getUtcOffsetsForState()
          .then(( utcOffsets : Array<UtcOffset> ) => {
            this.utcOffsets = utcOffsets;

            if ( utcOffsets.length === 1 ) {
              this.companySettings.utc_description = utcOffsets[0].code;

              this.updateUtcSettings();
            }

            if (this.companyService.hasMultiTax()) {
              this.getAndSetTaxGroup();
            }
          });
        }
      });
    }
  }

  flip ( side : FlipperSidesModel = 'back' ) : void {
    this.activeSide = side;

    this.changeDetectorRef.detectChanges();
  }

  flipToBack () : void {
    this.frontBlock.start();

    this.apiService.post('/company/set_region_tax_rate', {
      country_code: this.company.country_code,
      state       : this.company.state
    })
    .then(() => this.apiService.get('/company'))
    .then(( company : any ) => {
      this.company.tax_group_id       = company.tax_group_id;
      this.company.tax_group_name     = company.tax_group_name;
      this.company.tax_name           = company.tax_name;
      this.company.purchases_tax_rate = company.purchases_tax_rate;
      this.company.sales_tax_rate     = company.sales_tax_rate;

      return Promise.resolve(this.company);
    })
    .then(() => this.companyService.hasMultiTax()
      ? this.getAndSetTaxGroup()
      : Promise.resolve(true)
    )
    .then(() => {
      this.flip();
    })
    .finally(() => {
      this.frontBlock.stop();
    });
  }

  formatTimeToDateTime ( time : string ) : any {
    return moment(time, this.timeFormat)
      .format(this.dateTimeFormat);
  }

  getAndSetTaxGroup () : Promise<any> {
    return this.apiService.get('/tax_groups')
    .then(( groups : Array<any> ) => {
      this.taxGroup = groups.find(group => group.id === this.company.tax_group_id);

      this.taxGroup.tax_group_items_attributes.forEach(( item : any ) => {
        if (this.isStateTax(item)) {
          this.taxGroup.stateTaxRate = item.tax_rate;
        }

        else if (this.isLocalTax(item)) {
          this.taxGroup.localTaxRate = item.tax_rate;
        }
      });

      return this.taxGroup;
    });
  }

  getStates ( country = this.company.country_code) : Promise<Array<CodeTypeModel>> {
    return this.chargifyService.getStates(country);
  }

  getUtcOffsetsForState ( state = this.company.state ) : Promise<Array<UtcOffset>> {
    return this.apiService.get('/company/get_utc_offset_type', [
      this.company.country_code,
      state
    ]);
  }

  getValidations ( country = this.company.country_code ) : Promise<CompanyCountryValidationsModel> {
    return this.apiService.get('/system/country_validations', country);
  }

  handleStateChange ( state = this.company.state ) : void {
    this.frontBlock.start();

    this.getUtcOffsetsForState(state)
    .then(( utcOffsets : Array<UtcOffset> ) => {
      this.utcOffsets = utcOffsets;

      if ( utcOffsets.length === 1 ) {
        this.companySettings.utc_description = utcOffsets[0].code;

        this.updateUtcSettings();
      }
    })
    .finally(() => {
      this.frontBlock.stop();
    });
  }

  isLocalTax ( item : any = {} ) : boolean {
    return item.tax_type === 'Local Tax'
      || item.tax_type === 'GST';
  }

  isStateTax ( item : any = {} ) : boolean {
    return item.tax_type === 'State Tax'
      || item.tax_type === 'PST'
      || item.tax_type === 'QST';
  }

  save () : Promise<any> {
    this.backBlock.start();

    return this.apiService.patch('/company', this.company, 'company')
    .then(() => this.apiService.patch('/company_settings', this.companySettings, 'company_setting'))
    .then(() => {
      if (this.companyService.hasMultiTax()) {
        this.taxGroup.tax_group_items_attributes.forEach(( item : any ) => {
          if (this.isStateTax(item)) {
            item.tax_rate = this.taxGroup.stateTaxRate;
          }

          else if (this.isLocalTax(item)) {
            item.tax_rate = this.taxGroup.localTaxRate;
          }
        });

        return this.apiService.patch('/tax_group', this.taxGroup, 'tax_group');
      }

      Promise.resolve(this.taxGroup);
    })
    .then(( data : any ) => {
      this.activeModal.close(data);

      this.router.stateService.reload();
    })
    .catch(( err : any ) => {
      this.activeModal.dismiss(err);
    })
    .finally(() => {
      this.backBlock.stop();
    });
  }

  selectTime ( event : Event, property : string ) : void {
    const picker = $(event.currentTarget).datetimepicker({
      initialDate : new Date(),
      showMeridian: true,
      startView   : 'day'
    })
    .on('changeDate', ( ev : any ) => {
      let date : Date | string = new Date(ev.date.valueOf());

      date = new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes()
      ).toISOString();

      this[property] = date;

      if ( property === 'shopCloses' ) {
        this.company.shop_closes = moment(date).format(this.timeFormat);
      }
      else if ( property === 'shopCloses' ) {
        this.company.shop_opens = moment(date).format(this.timeFormat);
      }

      this.changeDetectorRef.detectChanges();

      $(picker).datetimepicker('hide');
    });

    $(picker).datetimepicker('show');
  }

  setCountryDefaults ( country = this.company.country_code ) : void {
    this.frontBlock.start();

    this.company.state                   = null;
    this.company.utc_offset              = null;
    this.companySettings.utc_description = null;

    this.getStates(country)
    .then(( states : Array<CodeTypeModel> ) => {
      this.states = states;

      return this.getValidations(country);
    })
    .then(( validations : CompanyCountryValidationsModel ) => {
      this.validations = validations;

      return Promise.resolve(validations);
    })
    .finally(() => {
      this.frontBlock.stop();
    });
  }

  updateUtcSettings ( utcDescription = this.companySettings.utc_description ) : void {
    this.company.utc_offset = this.companyService.getUtcOffsetFromCode(utcDescription, this.utcOffsets);
  }
}